import {
    DestroyRef,
    Injectable,
    type Signal,
    assertInInjectionContext,
    computed,
    inject,
    signal,
} from "@angular/core";
import type { IClient } from "../../../shared/model/client.model";
import type { IProject } from "../../../shared/model/project.model";

export interface TimerPrefill {
    readonly client?: IClient;
    readonly project?: IProject;
}

interface Registration {
    readonly prefill: Signal<TimerPrefill>;
}

/**
 * The client and project that timer controls outside the current page prefill
 * from. Pages register through {@link prefillTimers}; while several are alive
 * mid-navigation, the most recently registered one wins.
 */
@Injectable({ providedIn: "root" })
export class TimerPrefillRegistry {
    private readonly registrations = signal<readonly Registration[]>([]);

    readonly current = computed<TimerPrefill>(
        () => this.registrations().at(-1)?.prefill() ?? {},
    );

    register(prefill: Signal<TimerPrefill>): () => void {
        const registration: Registration = { prefill };
        this.registrations.update((registrations) => [
            ...registrations,
            registration,
        ]);
        return () =>
            this.registrations.update((registrations) =>
                registrations.filter((entry) => entry !== registration),
            );
    }
}

/**
 * Declares the calling page's client and project as the prefill for timer
 * controls outside the page, for as long as the caller lives. Call it from a
 * constructor or field initializer, and read signals in `getPrefill` so the
 * prefill follows the page's data.
 */
export function prefillTimers(getPrefill: () => TimerPrefill): void {
    assertInInjectionContext(prefillTimers);
    const unregister = inject(TimerPrefillRegistry).register(
        computed(getPrefill),
    );
    inject(DestroyRef).onDestroy(unregister);
}
